// import { browser, building, dev, version } from '$app/environment';
import * as Sentry from '@sentry/sveltekit';
import { HeimdallService, MtmService, GovHeimdallService } from '$lib/api';
import { persisted } from 'svelte-local-storage-store';
import { derived, get, writable, type Writable } from 'svelte/store';
import { goto } from '$app/navigation';
import { browser } from '$app/environment';
import type { Readable } from 'svelte/motion';
import { resetMeta } from '$stores/meta';
import { transformUrl, setLocale } from '$stores/master';
import dayjs from 'dayjs';
import { setThemeConfig, themeStore, updateThemeConfig } from '$stores/theme';
import posthog from 'posthog-js';
import { _ } from 'svelte-i18n';
import { isAxiosError } from 'axios';
import {
    createAuthRequestKey,
    createInFlightRequestStore,
    parseWorkspaceThemeConfig,
    shouldApplyWorkspaceScopedAuthRequestResult,
    shouldCacheWorkspaceThemeConfig,
    shouldResetAuthOnRefreshError,
} from './auth.utils';

export type Auth = {
    userInfo: UserInfo;
    workspaceUserInfo?: WorkspaceUserInfo;
    workspace?: WorkspaceInfo & { subscriptionStatus?: MtmLauncherDto['subscriptionStatus'] };
    launcher?: MtmLauncherDto[];
    workspaceId?: number;
    tenantId?: number;
    workspaceName?: string;
    permissions: Array<HeimdallPermissionDto>;
    hasPermission: (permission: MtmPermissionDto['name']) => boolean;
    somePermissions: (permissionNames: Array<MtmPermissionDto['name']>) => boolean;
    products: MtmProductDto[];
    productMap: Record<string, MtmProductDto>;
    hasProduct: (productCode: MtmProductDto['type']) => boolean; // 해당 워크스페이스에 해당 제품이 있는지 확인
    hasProductRole: (productCode: MtmProductDto['type']) => boolean; // 해당 워크스페이스 해당 제품의 역할을 현재 사용자가 가지고 있는지 확인
    currentSubscription: MtmSubscriptionInfoDto & { planDisplayName: string; planContractTypeMaxCount: number; planValue: string };
};

export type UserInfo = Partial<MtmMyInfoDto & { alertLevel?: 'NONE' | 'WEAK' | 'STRONG'; createdAt?: string }>;
export type WorkspaceUserInfo = Partial<MtmMyWorkspaceInfoDtoV2>;
export type WorkspaceInfo = Partial<MtmWorkspaceDto>;

// 유저 정보는 persisted로 localstorage에 자동 저장
export const userInfo: Writable<UserInfo> = persisted('userInfo', {});

//임시 코드, 기존 로그인 정보를 지우기 위함
if (browser) {
    window.localStorage.removeItem('auth');
}

// State
const initialValue: Partial<Auth> = {
    launcher: [],
    workspaceId: 0,
    tenantId: 0,
    permissions: [],
};

const REFRESH_TOKEN_TIMING_STORAGE_KEY = 'refreshTokenTiming';
const AUTH_BOOTSTRAP_REQUEST_CONFIG = {
    timeout: 30000,
    requestContext: 'auth-bootstrap',
};
const REFRESH_TOKEN_REQUEST_CONFIG = {
    requestContext: 'refresh-token',
};
const capturedThemeDiagnostics = new Set<string>();
const authRequestStore = createInFlightRequestStore<unknown>();
let authRequestGeneration = 0;
let cachedWorkspaceConfigId = 0;

type AuthRequestType = 'refresh-user-info' | 'refresh-workspace-info' | 'refresh-token';
type AuthRequestConfig = RequestConfig & {
    authRequestGeneration?: number;
    authRequestWorkspaceId?: number;
};

const getNextAuthRequestGeneration = () => {
    authRequestGeneration += 1;
    return authRequestGeneration;
};

const getCurrentAuthRequestGeneration = () => authRequestGeneration;

const createAuthRequestConfig = (requestConfig: RequestConfig = {}, requestGeneration = getCurrentAuthRequestGeneration(), workspaceId = get(auth).workspaceId ?? 0): AuthRequestConfig => {
    return {
        ...requestConfig,
        authRequestGeneration: requestGeneration,
        authRequestWorkspaceId: workspaceId,
    };
};

const shouldUseAuthRequestResult = (requestConfig?: AuthRequestConfig) => {
    return shouldApplyWorkspaceScopedAuthRequestResult(
        requestConfig?.authRequestGeneration ?? getCurrentAuthRequestGeneration(),
        getCurrentAuthRequestGeneration(),
        requestConfig?.authRequestWorkspaceId ?? 0,
        get(auth).workspaceId ?? 0,
    );
};

// workspaceId = 0 은 "workspace 무관" 전역 요청을 뜻함 (예: actionRefreshUserInfo)
// 양수 workspaceId는 해당 workspace에 한정된 요청 (예: fetchRefreshWorkspaceInfo)
const runScopedAuthRequest = <T>(requestType: AuthRequestType, factory: (requestConfig: AuthRequestConfig) => Promise<T>, workspaceId = 0) => {
    const requestGeneration = getCurrentAuthRequestGeneration();
    const requestKey = createAuthRequestKey(requestGeneration, requestType, workspaceId);
    const requestConfig = createAuthRequestConfig({}, requestGeneration, workspaceId);

    return authRequestStore.run(requestKey, () => factory(requestConfig)) as Promise<T>;
};
const updateRefreshTokenTiming = (headers: Record<string, unknown>) => {
    if (!browser || !headers) return;

    const refreshAfterHeader = headers['x-refresh-after'] ?? headers['X-Refresh-After'];
    if (typeof refreshAfterHeader !== 'string' || !refreshAfterHeader.trim()) return;

    // ex: 2026-02-23T14:41:46.999+09:00 ISO 형식으로 저장
    localStorage.setItem(REFRESH_TOKEN_TIMING_STORAGE_KEY, refreshAfterHeader);
};

export const workspaceInfo: Writable<Partial<Auth>> = writable({ ...initialValue });
export const auth: Readable<Auth> = derived([workspaceInfo, userInfo], ([$workspaceInfo, $userInfo], set) => {
    const hasPermission = (permissionName: MtmPermissionDto['name']) => (get(workspaceInfo).permissions || []).some(pm => pm.name === permissionName);
    const currentWorkspaceId = $workspaceInfo.workspaceId;
    const currentWorkspace = $workspaceInfo.launcher?.find(ws => ws.workspaceId === currentWorkspaceId);
    const currentWorkspaceInfo = currentWorkspace
        ? {
              ...currentWorkspace,
              id: currentWorkspace.workspaceId,
              memo: currentWorkspace.workspaceMemo,
              name: currentWorkspace.workspaceName,
              tenantId: currentWorkspace.tenantId,
              subscriptionStatus: currentWorkspace.subscriptionStatus,
          }
        : {};
    const currentWorkspaceUserInfo = {
        name: currentWorkspace?.displayName || $userInfo.name || '',
        id: currentWorkspace?.userId || $userInfo.id,
        loginId: $userInfo.loginId || '',
        workspaceName: currentWorkspace?.workspaceName || '',
        displayName: currentWorkspace?.displayName || $userInfo.name || '',
        jobPositionName: currentWorkspace?.jobPositionName || '',
        divisionName: currentWorkspace?.divisionName || '',
        jobTitleName: currentWorkspace?.jobTitleName || '',
    };
    const currentWorkspaceProducts = currentWorkspace?.products || [];
    const productMap = currentWorkspaceProducts.reduce((acc, product) => {
        acc[product.type] = product;
        return acc;
    }, {});

    set({
        ...$workspaceInfo,
        userInfo: $userInfo,
        workspace: currentWorkspaceInfo,
        workspaceUserInfo: currentWorkspaceUserInfo,
        products: currentWorkspaceProducts,
        productMap,
        hasPermission,
        currentSubscription: $workspaceInfo.currentSubscription,
        somePermissions: (permissionNames: Array<MtmPermissionDto['name']>) => permissionNames.some(permissionName => hasPermission(permissionName)),
        hasProduct: (productCode: MtmProductDto['type']) => {
            return currentWorkspaceProducts?.some(product => product.type === productCode);
        },
    } as Auth);
});

export const session = derived(auth, $auth => {
    return !!$auth.userInfo?.id;
});

// 비밀번호 갱신 여부 확인, actionSignIn 에서 할당 됨
export const alertLevel = derived(auth, $auth => {
    return $auth.userInfo?.alertLevel;
});

// 비밀번호 갱신 연기
export const passwordPostpone = async () => {
    const res = await HeimdallService.postponePasswordChange();
    if (res.data.code === 'SUCCESS') {
        // alertLevel:
        // NONE: 비밀번호 갱신 안해도 됨
        // WEAK: 비밀번호 갱신 연기 가능
        // STRONG: 비밀번호 무조건 갱신 해야
        userInfo.update($userInfo => ({ ...$userInfo, alertLevel: 'NONE' }));
    }
};

// 로그인
export const actionSignIn = async ({ loginId, password }: HeimdallLoginForm) => {
    try {
        await actionClearAuth();

        const res = await HeimdallService.login(
            {
                requestBody: { loginId, password },
            },
            { ignoreErrorMessage: true },
        );

        if (res.data.message) {
            alert(res.data.message);
            return false;
        }

        // persisted userInfo 에 alertLevel 추가
        userInfo.update($userInfo => ({ ...$userInfo, ...res.data.data }));

        workspaceInfo.set({ ...initialValue });

        await actionRefreshUserInfo();
        Sentry.setUser({
            email: get(auth).userInfo.loginId,
        });
        // 로그인 또는 리프레시 시에 아래 REFRESH_TOKEN_TIMING_STORAGE_KEY 값 갱신, 다음 리프레시 시점을 알기 위함.
        updateRefreshTokenTiming(res.headers);
    } catch (err) {
        Sentry.captureException(err);

        throw err;
    }
    return true;
};

type RefreshAuthResponse = Awaited<ReturnType<typeof HeimdallService.login>> | Awaited<ReturnType<typeof HeimdallService.ssoLogin>>;

export const actionSignInRefreshAuth = async (login: () => Promise<RefreshAuthResponse>) => {
    try {
        await actionClearAuth();

        const res = await login();

        workspaceInfo.set({ ...initialValue });

        await actionRefreshUserInfo();

        Sentry.setUser({ email: get(auth).userInfo.loginId });
        updateRefreshTokenTiming(res.headers);
        return res;
    } catch (err) {
        Sentry.captureException(err);

        throw err;
    }
};

export const fetchRefreshWorkspaceInfo = async () => {
    const workspaceId = get(auth).workspaceId ?? 0;

    await runScopedAuthRequest(
        'refresh-workspace-info',
        async requestConfig => {
            await Promise.all([
                getLauncher({ ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...requestConfig }),
                actionRefreshPermissions({ ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...requestConfig }),
                getWorkspaceConfig({ ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...requestConfig }),
            ]);
        },
        workspaceId,
    );
};

const captureThemeDiagnostic = (workspaceId: number | undefined, theme: unknown, error: unknown) => {
    const diagnosticKey = `${workspaceId || 0}:${typeof theme}:${String(theme).slice(0, 120)}`;
    if (capturedThemeDiagnostics.has(diagnosticKey)) return;

    capturedThemeDiagnostics.add(diagnosticKey);
    Sentry.captureException(error, {
        extra: {
            workspaceId: workspaceId || null,
            themeType: typeof theme,
        },
        fingerprint: ['workspace-theme-config-parse', String(workspaceId || 0)],
    });
};

const getWorkspaceConfig = async (requestConfig: AuthRequestConfig = {}) => {
    const workspaceId = requestConfig.authRequestWorkspaceId ?? get(auth).workspaceId;

    if (cachedWorkspaceConfigId === workspaceId) return;

    await setThemeConfig(__CONTRACT_THEME_CONFIG__);

    try {
        const configRes = await MtmService.getCurrentWorkspaceConfig({}, { ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...requestConfig });
        if (configRes.data.code === 'SUCCESS') {
            const theme = configRes.data.data.theme;
            const themeConfig = parseWorkspaceThemeConfig(theme);

            if (!themeConfig) {
                if (typeof theme === 'string' && theme.trim()) {
                    captureThemeDiagnostic(workspaceId, theme, new Error('Invalid workspace theme config'));
                }
                if (shouldUseAuthRequestResult(requestConfig) && shouldCacheWorkspaceThemeConfig(theme, themeConfig)) {
                    cachedWorkspaceConfigId = workspaceId ?? 0;
                }
                return;
            }

            if (!shouldUseAuthRequestResult(requestConfig)) return;

            await updateThemeConfig(themeConfig);
            if (shouldCacheWorkspaceThemeConfig(theme, themeConfig)) {
                cachedWorkspaceConfigId = workspaceId ?? 0;
            }
        }
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

// workspaceName을 이용하여 tenantId, workspaceId를 가져옴
export const extractWorkspaceInfo = (workspaceName: string) => {
    const [tenantId, workspaceId] = workspaceName.split('-').map(Number);
    return { tenantId: tenantId ?? 0, workspaceId: workspaceId ?? 0 };
};

// tenantId, workspaceId를 이용하여 workspaceName을 가져옴
export const makeWorkspaceName = (tenantId: number, workspaceId: number) => {
    return `${tenantId}-${workspaceId}`;
};

export const getLauncher = async (requestConfig: AuthRequestConfig = {}) => {
    // luancher API 내부에는 각 워크스페이스에 대한 정보와 각 워크스페이스에서의 유저 정보가 포함되어 있음
    try {
        const {
            data: { data, code },
        } = await MtmService.getLauncher({}, { ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...requestConfig });
        if (code === 'SUCCESS') {
            if (!shouldUseAuthRequestResult(requestConfig)) return [];
            workspaceInfo.update($auth => ({ ...$auth, launcher: data || [] }));
            return data;
        }
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
    return [] as MtmLauncherDto[];
};

export async function getCurrentSubscription() {
    try {
        const [subscription, planPolicies] = await Promise.all([MtmService.getCurrentSubscription().then(res => res.data.data), MtmService.getCurrentPlanPolicies().then(res => res.data.data)]);

        let planDisplayName = '';
        const planContractTypeMaxCount = planPolicies.standardFormMaxCount === 99999999 ? Infinity : planPolicies.standardFormMaxCount;
        let planValue = '';

        if (subscription.status !== 'ACTIVE') {
            // 구독 상태가 ACTIVE가 아니면
            planValue = '';
            planDisplayName = '';
        } else if (subscription.planType === 'TRIAL') {
            planValue = 'TRIAL';
            planDisplayName = 'Trial';
        } else if (subscription.planTags.includes('BASIC')) {
            planValue = 'BASIC';
            planDisplayName = 'Basic';
        } else if (subscription.planTags.includes('LITE')) {
            planValue = 'LITE';
            planDisplayName = 'Core';
        } else if (subscription.planTags.includes('STANDARD')) {
            planValue = 'STANDARD';
            planDisplayName = 'Standard';
        } else if (subscription.planTags.includes('PRO')) {
            planValue = 'PRO';
            planDisplayName = 'Pro';
        } else {
            planValue = subscription.planType;
            planDisplayName = subscription.planName;
        }

        workspaceInfo.update($workspaceInfo => ({
            ...$workspaceInfo,
            currentSubscription: {
                ...subscription,
                planDisplayName,
                planContractTypeMaxCount,
                planValue,
                // 구독 상태가 ACTIVE가 아니면 planType을 ''로 설정
                planType: subscription.status === 'ACTIVE' ? subscription.planType : ('' as any),
            },
        }));

        return subscription;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
    return null;
}

// 사용자 권한 목록
export const actionRefreshPermissions = async (requestConfig: AuthRequestConfig = {}) => {
    const workspaceId = requestConfig.authRequestWorkspaceId ?? get(auth).workspaceId;

    try {
        const res = await HeimdallService.listWorkspaceUserPermissionsV3({}, { ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...requestConfig });
        if (res.data.code !== 'SUCCESS') return;

        const newPermissions = res.data.data || [];
        if (!shouldUseAuthRequestResult(requestConfig)) return;

        if (!workspaceId) {
            if (!newPermissions.some(p => p.name === 'super_admin.workspace.read')) return;
        }

        workspaceInfo.update($workspaceInfo => ({
            ...$workspaceInfo,
            permissions: newPermissions,
        }));
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

// 사용자 정보 갱신
export const actionRefreshUserInfo = async (options: RequestConfig = { ignoreErrorMessage: false }) => {
    return runScopedAuthRequest<void>('refresh-user-info', async requestConfig => {
        try {
            const myInfoRes = await MtmService.getMyInfo(undefined, { ...AUTH_BOOTSTRAP_REQUEST_CONFIG, ...options, ...requestConfig });
            if (myInfoRes.data.code === 'SUCCESS') {
                const myInfo = myInfoRes.data.data;
                if (!shouldUseAuthRequestResult(requestConfig)) return;
                userInfo.update($userInfo => ({ ...$userInfo, ...myInfo }));
                const userPreferredLocale = get(auth).userInfo.locale || '';
                await setLocale(userPreferredLocale);

                if (get(themeStore).sendAnalyticsEvent && !import.meta.env.DEV) setPostHogUser(myInfo.loginId);
            }
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    });
};

// 로그아웃
export const signOut = async () => {
    try {
        await HeimdallService.logout();
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
    await actionClearAuth();
    Sentry.setUser(null);
    if (get(themeStore).sendAnalyticsEvent && !import.meta.env.DEV) posthog.reset();
    resetMeta();
    await setLocale();
};

// 로그아웃 후 페이지 이동
export const actionSignOut = (url?: string) =>
    signOut().then(() =>
        goto(transformUrl(url ?? '/sign-in'), {
            replaceState: true,
        }),
    );

export const actionClearAuth = async () => {
    getNextAuthRequestGeneration();
    authRequestStore.clear();
    capturedThemeDiagnostics.clear();
    userInfo.set({});
    workspaceInfo.set({
        ...initialValue,
    });
    cachedWorkspaceConfigId = 0;

    localStorage.removeItem(REFRESH_TOKEN_TIMING_STORAGE_KEY);
};

export async function setupAuthFromExternal(requestConfig?: RequestConfig) {
    try {
        if (!get(session)) {
            // 로그인 한 적은 없지만 세션이 있을 수 있는 경우(서브도메인 등에서 로그인한 경우)의 로그인 상태 유지를 위한 세션 갱신 시도
            await actionRefreshUserInfo({ ...requestConfig, ignoreErrorMessage: true });
        }
        Sentry.setUser({
            email: get(auth).userInfo.loginId,
        });
        const userPreferredLocale = get(auth).userInfo.locale || '';
        if (userPreferredLocale) await setLocale(userPreferredLocale);
    } catch (err) {
        // 갱신을 못한다고 해서, 에러는 아니므로 센트리는 생략
        console.log(err);
    }
}

export function shouldRefreshToken() {
    if (!browser) return false; // 브라우저가 아니면 즉시 false 반환 (SSR에서는 리프레시 불필요

    const refreshTokenTiming = localStorage.getItem(REFRESH_TOKEN_TIMING_STORAGE_KEY);
    if (!refreshTokenTiming) {
        return false; // 리프레시 타이밍 정보가 없으면 즉시 false 반환
    }

    const isLogin = get(session);
    if (!isLogin) {
        localStorage.removeItem(REFRESH_TOKEN_TIMING_STORAGE_KEY); // 로그인 상태가 아니면 리프레시 타이밍 정보를 삭제하고 false 반환
        return false;
    }

    const parsedRefreshTiming = dayjs(refreshTokenTiming);
    if (!parsedRefreshTiming.isValid()) {
        localStorage.removeItem(REFRESH_TOKEN_TIMING_STORAGE_KEY);
        return false;
    }

    const isTokenExpired = dayjs().isAfter(parsedRefreshTiming);
    const isTabActive = document.visibilityState === 'visible';

    return isTokenExpired && isTabActive && isLogin; // 토큰이 만료되었고, 탭이 활성 상태이고, 로그인 상태이면 true 반환
}

export async function refreshToken() {
    return runScopedAuthRequest<void>('refresh-token', async requestConfig => {
        try {
            const res = await HeimdallService.refreshV2({}, { ...REFRESH_TOKEN_REQUEST_CONFIG, ...requestConfig });
            if (!shouldUseAuthRequestResult(requestConfig)) return;
            updateRefreshTokenTiming(res.headers);
        } catch (error: unknown) {
            const responseStatus = isAxiosError(error) ? error.response?.status : undefined;
            if (shouldUseAuthRequestResult(requestConfig) && shouldResetAuthOnRefreshError(responseStatus)) {
                actionClearAuth();
                resetMeta();
            }

            Sentry.captureException(error);
            // await goto(transformUrl('/sign-in'), { replaceState: true });
        }
    });
}

/**
 * CSAP관련 코드
 */

// 계정 잠금 정보 추출 함수
export const extractLockInfo = (error: any) => {
    if (!error) return null;

    try {
        const errorData = error.response?.data?.data;
        if (!errorData) return null;

        const { currentRetryCount, maxRetryCount, lockedUntil } = errorData;

        if (currentRetryCount !== undefined || maxRetryCount !== undefined || lockedUntil) {
            return {
                currentRetryCount,
                maxRetryCount,
                lockedUntil,
            };
        }
    } catch (e) {
        console.error('Error extracting lock info:', e);
    }

    return null;
};

// OTP 재전송 함수
export const actionResendOtp = async ({ loginId, password }: { loginId: string; password: string }, { isSuperAdmin = false } = {}) => {
    try {
        // 로그인 API를 다시 호출하여 새로운 OTP 발송 (비밀번호는 빈 문자열로 전송)
        const res = await (isSuperAdmin
            ? GovHeimdallService.loginForSuperAdminV3({ requestBody: { loginId, password } }, { ignoreErrorMessage: true })
            : GovHeimdallService.loginV3({ requestBody: { loginId, password } }, { ignoreErrorMessage: true }));

        if (res.data.code !== 'SUCCESS' || res.data.data.twoFactorAuth?.method !== 'OTP_MAIL') {
            return {
                success: false,
                message: get(_)('autogen.contract.auth.resendOtpFailed'),
            };
        }

        return {
            success: true,
            twoFactorVerificationToken: res.data.data.twoFactorAuth.token || '',
        };
    } catch (err) {
        Sentry.captureException(err);
        return { success: false, error: err };
    }
};

// OTP로 로그인 처리 함수
export const actionSignInWithOtp = async ({ otpCode, twoFactorVerificationToken }: { otpCode: string; twoFactorVerificationToken: string }, { isSuperAdmin = false } = {}) => {
    try {
        // otp 로그인 응답에는 아무것도 없고, otp 이전의 로그인에서의 응답들이 다 clear 되기 때문에, 아래는 제거 되어야 할 것 같음
        // await actionClearAuth();

        const res = await (isSuperAdmin
            ? GovHeimdallService.verifyOtpForSuperAdminLogin(
                  {
                      requestBody: { otp: otpCode },
                      xTwoFactorVerificationToken: twoFactorVerificationToken,
                  },
                  { ignoreErrorMessage: true },
              )
            : GovHeimdallService.verifyOtpForLogin(
                  {
                      requestBody: { otp: otpCode },
                      xTwoFactorVerificationToken: twoFactorVerificationToken,
                  },
                  { ignoreErrorMessage: true },
              ));

        if (res.data.code !== 'SUCCESS') {
            return { success: false, message: res.data.message };
        }

        await completeLoginProcess();
        return { success: true };
    } catch (err) {
        Sentry.captureException(err);
        return { success: false, error: err };
    }
};

// 로그인 후 공통 처리 함수
const completeLoginProcess = async () => {
    workspaceInfo.set({ ...initialValue });

    await actionRefreshUserInfo();

    Sentry.setUser({
        email: get(auth).userInfo.loginId,
    });
};

// 로그인
export const actionSignInCsap = async ({ loginId, password }: { loginId: string; password: string }, { isSuperAdmin = false } = {}) => {
    try {
        await actionClearAuth();

        const res = await (isSuperAdmin
            ? GovHeimdallService.loginForSuperAdminV3({ requestBody: { loginId, password } }, { ignoreErrorMessage: true })
            : GovHeimdallService.loginV3({ requestBody: { loginId, password } }, { ignoreErrorMessage: true }));

        if (res.data.code !== 'SUCCESS') {
            return { success: false, message: res.data.message };
        }

        // persisted userInfo 에 alertLevel 추가
        userInfo.update($userInfo => ({ ...$userInfo, alertLevel: (res.data.data as any).alertLevel || (res.data.data as any).passwordAlertLevel }));

        if (res.data.data.twoFactorAuth?.method === 'OTP_MAIL') {
            return {
                success: true,
                twoFactorAuthRequired: true,
                twoFactorVerificationToken: res.data.data.twoFactorAuth.token,
            };
        }

        await completeLoginProcess();
        return { success: true };
    } catch (err) {
        Sentry.captureException(err);
        return { success: false, error: err };
    }
};

// Product 팀 요청으로 삽입된 코드
// https://bhsn.atlassian.net/browse/CLMELM-133?atlOrigin=eyJpIjoiYjdiMDA4NDhmZDUxNDg3YjlkMDQ3M2ViZTIyMWU3YzAiLCJwIjoiaiJ9
async function setPostHogUser(userEmail: string) {
    const domainName = userEmail.split('@')[1] ?? 'unknown';

    //product 팀 요청으로 원문 그대로 받아서 사용할 수 있도록 적용
    const group = domainName;

    posthog.setPersonProperties({ group });
    posthog.group('company', group, {
        name: group,
    });
}
